import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Image } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ActiveFilterChips, DataTable, DeleteModal } from "CommonComponent";
import {
  fetchAppraisals,
  getAppraisalById,
  deleteAppraisal,
  resetAppraisalSetupFlow,
} from "actions";
import AccessRights from "utils/AccessRights";
import { Columns } from "./component";
import Filter from "./component/Filter/Filter";
import {
  EMPTY_SETUP_FILTERS,
  applySetupFilters,
  buildSetupActiveFilterChips,
  buildSetupSearchParamsFromState,
  parseSearchParamsToSetupFilters,
  resolveSetupFilterLabels,
  sortFromSetupUrlParams,
} from "./utils/setupListQuery";

const AppraisalSetupList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const appraisalState = useSelector((s) => s.appraisalState);
  const session = useSelector((s) => s.session);
  const employeeState = useSelector((s) => s.employeeState);
  const skipUpdateNavOnMountRef = useRef(true);
  const skipViewNavOnMountRef = useRef(true);
  const skipUrlEffectRef = useRef(false);
  const filterOptionsReady = true;

  const object_viewed_id = session?.current_page_permissions?.object_id || "";

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [appraisalIdToDelete, setAppraisalIdToDelete] = useState("");
  const [refershDataTable, setRefershDataTable] = useState(false);
  const [filters, setFilters] = useState(() =>
    parseSearchParamsToSetupFilters(searchParams)
  );
  const [tableQuery, setTableQuery] = useState(() => ({
    page: 1,
    pageSize: 10,
    search: searchParams.get("q") || "",
    sort: sortFromSetupUrlParams(searchParams),
  }));

  const resolvedFilters = useMemo(
    () =>
      resolveSetupFilterLabels(filters, {
        campaigns: employeeState.campaignsList || [],
        designations: employeeState.designationsList || [],
        offices: employeeState.officesList || [],
      }),
    [
      filters,
      employeeState.campaignsList,
      employeeState.designationsList,
      employeeState.officesList,
    ]
  );

  const syncListUrl = useCallback(
    (nextFilters, nextTableQuery) => {
      setFilters(nextFilters);

      const params = buildSetupSearchParamsFromState({
        filters: nextFilters,
        sort: nextTableQuery?.sort,
        search: nextTableQuery?.search,
      });

      if (params.toString() === searchParams.toString()) {
        return;
      }

      skipUrlEffectRef.current = true;
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    if (skipUrlEffectRef.current) {
      skipUrlEffectRef.current = false;
      return;
    }

    const nextFilters = parseSearchParamsToSetupFilters(searchParams);
    const nextSort = sortFromSetupUrlParams(searchParams);
    const nextSearch = searchParams.get("q") || "";

    setFilters(nextFilters);
    setTableQuery((prev) => ({
      ...prev,
      search: nextSearch,
      sort: nextSort,
      page: 1,
    }));
  }, [searchParams]);

  useEffect(() => {
    dispatch(fetchAppraisals(object_viewed_id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(resetAppraisalSetupFlow());
  }, [dispatch]);

  useEffect(() => {
    if (skipUpdateNavOnMountRef.current) {
      skipUpdateNavOnMountRef.current = false;
      return;
    }
    if (appraisalState.showUpdateForm) {
      navigate("/appraisalSetup/update");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appraisalState.showUpdateForm]);

  useEffect(() => {
    if (skipViewNavOnMountRef.current) {
      skipViewNavOnMountRef.current = false;
      return;
    }
    if (appraisalState.showViewPage) {
      navigate("/appraisalSetup/view");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appraisalState.showViewPage]);

  const deleteRecord = async () => {
    await dispatch(
      deleteAppraisal(
        appraisalIdToDelete,
        session.current_page_permissions.object_id
      )
    );
    dispatch(fetchAppraisals(object_viewed_id));
    setOpenDeleteModal(false);
    setAppraisalIdToDelete("");
  };

  const showDeleteModal = (id) => {
    setAppraisalIdToDelete(id);
    setOpenDeleteModal(true);
  };

  const hideDeleteModal = () => {
    setAppraisalIdToDelete("");
    setOpenDeleteModal(false);
  };

  const updateRecord = (id) => dispatch(getAppraisalById(id, "update"));
  const viewRecord = (id) => dispatch(getAppraisalById(id, "view"));

  const getCampaignsName = (campaigns = []) =>
    [...new Set((campaigns || []).map((c) => c?.client_name).filter(Boolean))].join(
      ", "
    );
  const getDesignationsName = (designations = []) =>
    [...new Set((designations || []).map((d) => d?.name).filter(Boolean))].join(
      ", "
    );

  const getActions = (row) => (
    <div
      className="actionClass"
      style={{ textAlign: "center", whiteSpace: "nowrap" }}
    >
      {AccessRights(
        session.current_page_permissions,
        "view",
        row.created_by
      ) ? (
        <Image
          src="/assets/images/view-icon.svg"
          width={20}
          className="me-2 cursor-pointer"
          title="View"
          onClick={() => viewRecord(row.id)}
          alt=""
          aria-hidden="true"
        />
      ) : null}

      {AccessRights(
        session.current_page_permissions,
        "edit",
        row.created_by
      ) ? (
        <Image
          src="/assets/images/edit-icon.svg"
          width={20}
          className="me-2 cursor-pointer"
          title="Edit"
          onClick={() => updateRecord(row.id)}
          alt=""
          aria-hidden="true"
        />
      ) : null}

      {AccessRights(
        session.current_page_permissions,
        "delete",
        row.created_by
      ) ? (
        <Image
          src="/assets/images/delete-icon.svg"
          width={20}
          className="cursor-pointer"
          title="Delete"
          onClick={() => showDeleteModal(row.id)}
          alt=""
          aria-hidden="true"
        />
      ) : null}
    </div>
  );

  const columns = useMemo(
    () =>
      Columns({
        getCampaignsName,
        getDesignationsName,
        getActions,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session.current_page_permissions, appraisalState.appraisalsList]
  );

  const filteredRows = useMemo(
    () => applySetupFilters(appraisalState.appraisalsList || [], resolvedFilters),
    [appraisalState.appraisalsList, resolvedFilters]
  );

  const activeChips = useMemo(
    () => buildSetupActiveFilterChips(resolvedFilters, tableQuery.sort),
    [resolvedFilters, tableQuery.sort]
  );

  const handleFilterChange = (newFilters) => {
    let nextTableQuery;
    setTableQuery((prev) => {
      nextTableQuery = { ...prev, page: 1 };
      return nextTableQuery;
    });
    syncListUrl(newFilters, nextTableQuery);
  };

  const handleTableQueryChange = useCallback(
    (updater) => {
      setTableQuery((prev) => {
        const nextTableQuery =
          typeof updater === "function" ? updater(prev) : updater;

        const searchChanged = (prev.search ?? "") !== (nextTableQuery.search ?? "");
        const sortChanged =
          (prev.sort?.columnId ?? null) !== (nextTableQuery.sort?.columnId ?? null) ||
          (prev.sort?.direction ?? null) !== (nextTableQuery.sort?.direction ?? null);

        if (searchChanged || sortChanged) {
          syncListUrl(filters, nextTableQuery);
        }

        return nextTableQuery;
      });
    },
    [filters, syncListUrl]
  );

  const handleRemoveChip = (chip) => {
    if (chip.type === "sort") {
      handleTableQueryChange((prev) => ({ ...prev, sort: null, page: 1 }));
      return;
    }
    handleFilterChange({
      ...filters,
      [chip.key]: null,
    });
  };

  const handleClearAllFilters = () => {
    let nextTableQuery;
    setTableQuery((prev) => {
      nextTableQuery = { ...prev, sort: null, search: "", page: 1 };
      return nextTableQuery;
    });
    syncListUrl(EMPTY_SETUP_FILTERS, nextTableQuery);
  };

  return (
    <>
      <DataTable
        keyField="id"
        columns={columns}
        data={filteredRows}
        loading={appraisalState.loading}
        refreshTrigger={refershDataTable}
        setRefreshTrigger={setRefershDataTable}
        query={tableQuery}
        onControlledQueryChange={handleTableQueryChange}
        belowSearchElement={
          <ActiveFilterChips
            chips={activeChips}
            onRemoveChip={handleRemoveChip}
            onClearAll={handleClearAllFilters}
          />
        }
        filterElement={
          <Filter
            filters={resolvedFilters}
            onFiltersChange={handleFilterChange}
            loadFilterOptions={filterOptionsReady}
          />
        }
      />

      <DeleteModal
        open={openDeleteModal}
        onClose={hideDeleteModal}
        onConfirm={deleteRecord}
        title="Delete Appraisal Setup"
        message="Are you sure you want to delete this appraisal setup?"
      />
    </>
  );
};

export default AppraisalSetupList;
